/**
 * Form Sync Context
 *
 * React context for managing form synchronization state and operations.
 * Handles batching of updates, conflict resolution, and sync operations.
 *
 * @author Claude Code
 * @date 2025-09-22
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  FieldUpdateEvent,
  FieldUpdateBatch,
  FormSyncContextValue
} from '../types';
import {
  groupUpdatesByPrefix,
  getLatestUpdatesPerPath,
  cleanupOldUpdates
} from '../utils';

/**
 * Form Sync Context
 */
const FormSyncContext = createContext<FormSyncContextValue | null>(null);

/**
 * Form Sync Context Props
 */
export interface FormSyncProviderProps {
  /** Current field updates */
  fieldUpdates: FieldUpdateEvent[];

  /** Function to apply a field update */
  applyFieldUpdate: (update: FieldUpdateEvent) => void;

  /** Function to apply batch of updates */
  applyBatchUpdate?: (batch: FieldUpdateBatch) => void;

  /** Function to clear updates */
  clearUpdates?: () => void;

  /** Auto cleanup old updates (in ms) */
  autoCleanupTime?: number;

  /** Children to render */
  children: React.ReactNode;
}

/**
 * Form Sync Provider Component
 */
export function FormSyncProvider({
  fieldUpdates,
  applyFieldUpdate,
  applyBatchUpdate,
  clearUpdates,
  autoCleanupTime = 24 * 60 * 60 * 1000, // 24 hours
  children
}: FormSyncProviderProps) {

  // Get updates for a specific field path
  const getFieldUpdates = useCallback((path: string): FieldUpdateEvent[] => {
    return fieldUpdates.filter(update => update.path === path);
  }, [fieldUpdates]);

  // Get updates for a path prefix (for sections)
  const getSectionUpdates = useCallback((pathPrefix: string): FieldUpdateEvent[] => {
    return groupUpdatesByPrefix(fieldUpdates, pathPrefix);
  }, [fieldUpdates]);

  // Clear old updates
  const clearOldUpdates = useCallback((olderThan: number): void => {
    // This would typically be handled by the parent component
    // since we need to modify the state
    if (clearUpdates) {
      clearUpdates();
    }
  }, [clearUpdates]);

  // Automatically cleanup old updates periodically
  React.useEffect(() => {
    if (!autoCleanupTime) return;

    const interval = setInterval(() => {
      const cutoffTime = Date.now() - autoCleanupTime;
      const hasOldUpdates = fieldUpdates.some(
        update => update.timestamp < cutoffTime
      );

      if (hasOldUpdates) {
        clearOldUpdates(cutoffTime);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [fieldUpdates, autoCleanupTime, clearOldUpdates]);

  // Memoized context value
  const contextValue = useMemo<FormSyncContextValue>(() => ({
    fieldUpdates,
    applyFieldUpdate,
    getFieldUpdates,
    getSectionUpdates,
    clearOldUpdates
  }), [
    fieldUpdates,
    applyFieldUpdate,
    getFieldUpdates,
    getSectionUpdates,
    clearOldUpdates
  ]);

  return (
    <FormSyncContext.Provider value={contextValue}>
      {children}
    </FormSyncContext.Provider>
  );
}

/**
 * Hook to use form sync context
 */
export function useFormSyncContext(): FormSyncContextValue {
  const context = useContext(FormSyncContext);

  if (!context) {
    throw new Error('useFormSyncContext must be used within a FormSyncProvider');
  }

  return context;
}

/**
 * Hook to get real-time sync status
 */
export function useSyncStatus(): {
  hasUpdates: boolean;
  updateCount: number;
  lastUpdateTime: number | null;
  hasRecentUpdates: boolean;
} {
  const { fieldUpdates } = useFormSyncContext();

  return useMemo(() => {
    const now = Date.now();
    const hasRecentUpdates = fieldUpdates.some(
      update => now - update.timestamp < 5000
    );

    const lastUpdateTime = fieldUpdates.length > 0
      ? Math.max(...fieldUpdates.map(u => u.timestamp))
      : null;

    return {
      hasUpdates: fieldUpdates.length > 0,
      updateCount: fieldUpdates.length,
      lastUpdateTime,
      hasRecentUpdates
    };
  }, [fieldUpdates]);
}

/**
 * Hook to batch field updates
 */
export function useBatchUpdates(): {
  batchUpdate: (updates: FieldUpdateEvent[]) => void;
  pendingUpdates: FieldUpdateEvent[];
  flushUpdates: () => void;
} {
  const { applyFieldUpdate } = useFormSyncContext();
  const [pendingUpdates, setPendingUpdates] = React.useState<FieldUpdateEvent[]>([]);

  const batchUpdate = useCallback((updates: FieldUpdateEvent[]) => {
    setPendingUpdates(prev => [...prev, ...updates]);
  }, []);

  const flushUpdates = useCallback(() => {
    pendingUpdates.forEach(update => {
      applyFieldUpdate(update);
    });
    setPendingUpdates([]);
  }, [pendingUpdates, applyFieldUpdate]);

  // Auto-flush after a short delay
  React.useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const timeout = setTimeout(() => {
      flushUpdates();
    }, 100); // 100ms batch window

    return () => clearTimeout(timeout);
  }, [pendingUpdates, flushUpdates]);

  return {
    batchUpdate,
    pendingUpdates,
    flushUpdates
  };
}

/**
 * Hook for field update statistics
 */
export function useUpdateStatistics(): {
  totalUpdates: number;
  updatesBySource: Record<string, number>;
  userModifiedCount: number;
  externalUpdateCount: number;
  recentUpdateCount: number;
} {
  const { fieldUpdates } = useFormSyncContext();

  return useMemo(() => {
    const now = Date.now();
    const recentThreshold = 5000; // 5 seconds

    const stats = {
      totalUpdates: fieldUpdates.length,
      updatesBySource: {} as Record<string, number>,
      userModifiedCount: 0,
      externalUpdateCount: 0,
      recentUpdateCount: 0
    };

    fieldUpdates.forEach(update => {
      // Count by source
      stats.updatesBySource[update.source] = (stats.updatesBySource[update.source] || 0) + 1;

      // Count user modified
      if (update.isUserModified) {
        stats.userModifiedCount++;
      }

      // Count external updates
      if (update.source === 'firebase' || update.source === 'system' || update.source === 'ai') {
        stats.externalUpdateCount++;
      }

      // Count recent updates
      if (now - update.timestamp < recentThreshold) {
        stats.recentUpdateCount++;
      }
    });

    return stats;
  }, [fieldUpdates]);
}

/**
 * Context index export
 */
export * from './FieldUpdateContext';